// Real-time open/closed status for a Lokaso business.
// Guinea is UTC+0 year-round — we rely on the device clock anyway.

const DAY_KEYS = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];

/**
 * Parse "HH:MM" into minutes-since-midnight.
 */
function toMin(s) {
  if (!s) return NaN;
  const [h, m] = s.split(":").map((x) => Number(x));
  if (Number.isNaN(h)) return NaN;
  return h * 60 + (m || 0);
}

function fmtDelta(mins) {
  if (mins < 0) mins = 0;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (h <= 0) return `${m} min`;
  return `${h} h ${String(m).padStart(2, "0")} min`;
}

/**
 * Compute live open/closed status.
 *
 *   hours = { mon: "08:00-18:00", tue: "closed", ... }
 *
 * Returns one of:
 *   { state: "open",         label: "Ouvert · Ferme dans X h YY min" }
 *   { state: "opens_today",  label: "Fermé · Ouvre dans X h YY min" }
 *   { state: "closed_today", label: "Fermé aujourd'hui" }
 *   { state: "no_hours",     label: "Horaires non renseignés" }
 *
 * `dotClass` is a Tailwind background class for the leading status dot.
 */
export function computeLiveStatus(hours, now = new Date()) {
  if (!hours || typeof hours !== "object" || Object.keys(hours).length === 0) {
    return { state: "no_hours", label: "Horaires non renseignés", dotClass: "bg-stone-400" };
  }
  const todayKey = DAY_KEYS[now.getDay()];
  const todayStr = hours[todayKey];
  const cur = now.getHours() * 60 + now.getMinutes();

  if (!todayStr || todayStr === "closed") {
    return { state: "closed_today", label: "Fermé aujourd'hui", dotClass: "bg-rose-500" };
  }

  const [start, end] = String(todayStr).split("-").map((s) => s.trim());
  const startM = toMin(start);
  const endM = toMin(end);
  if (Number.isNaN(startM) || Number.isNaN(endM)) {
    return { state: "no_hours", label: "Horaires non renseignés", dotClass: "bg-stone-400" };
  }

  if (cur >= startM && cur < endM) {
    return {
      state: "open",
      label: `Ouvert · Ferme dans ${fmtDelta(endM - cur)}`,
      dotClass: "bg-emerald-500",
    };
  }
  if (cur < startM) {
    return {
      state: "opens_today",
      label: `Fermé · Ouvre dans ${fmtDelta(startM - cur)}`,
      dotClass: "bg-rose-500",
    };
  }
  return { state: "closed_today", label: "Fermé aujourd'hui", dotClass: "bg-rose-500" };
}
